import { useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson } from "../api";
import { BookingLoading, BookingProblem } from "../parts";
import { orderReturnTarget, orderState, type PaymentIntentStatus } from "./success/orderReturn";

// /sucessfullOrder/<payment intent>: where Stripe sends the customer back
// after paying. Port of v1's sucessfullOrder/[paymentIntentID] (page +
// SucessfullOrderClient): ask whether the order has been created
// (GET /app/checkPaymentIntentStatus/:id, no sign-in, as on v1), every 3
// seconds until it has, then send the customer to the return URL the API
// gives; if creating it failed, say so with the API's words. A failed
// re-check keeps asking, as v1's poll did.
//
// The return URL is built for v1 (the school's v1 widget host, the host
// the checkout started on, or the marketplace). A path on this host or on
// the school's own v1 host is followed on the current host, through the
// site's links (so a v2 customer stays on v2, with the page's link query);
// the marketplace's addresses are followed as v1 did (orderReturnTarget).
// v1 reads nothing from the page's query (Stripe's redirect_status
// included): the order's own status decides, and so it does here.

const POLL_MS = 3000;

export function OrderSuccessView({ ctx, paymentIntentId }: { ctx: ViewContext; paymentIntentId: string }) {
  const status = useQuery({
    queryKey: ["booking", "order-status", ctx.api, paymentIntentId],
    queryFn: () => bookingJson<PaymentIntentStatus>(`${ctx.api}/app/checkPaymentIntentStatus/${encodeURIComponent(paymentIntentId)}`),
    retry: (count, error) => count < 2 && !(error instanceof BookingApiError),
    refetchInterval: (query) => (query.state.data && orderState(query.state.data).kind !== "waiting" ? false : POLL_MS),
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
  });

  const state = status.data ? orderState(status.data) : null;
  const target = state?.kind === "done" ? orderReturnTarget(state.returnUrl, { origin: window.location.origin, businessSlug: ctx.businessSlug }) : null;
  // links.site adds only the link-query keys the return path lacks (the
  // API builds that path from the page the payment started on, query and all).
  const href = target ? (target.kind === "site" ? ctx.links.site(target.path) : target.href) : null;

  useEffect(() => {
    // v1's server answered a redirect: this address is replaced.
    if (href) window.location.replace(href);
  }, [href]);

  // The first answer decides the page; later failures are ignored while
  // there is one (the poll keeps going).
  if (!status.data) {
    if (status.isPending) return <BookingLoading rows={1} />;
    const notFound = status.error instanceof BookingApiError && status.error.status === 404;
    return notFound ? (
      <BookingProblem title="We couldn’t find this order" message="If you were charged, your confirmation email has the details." />
    ) : (
      <BookingProblem title="We couldn’t check your order" message="Please try again in a moment." onRetry={() => void status.refetch()} />
    );
  }

  if (state?.kind === "failed") {
    return (
      <Panel title="Sorry, your order was unsuccessful.">
        {state.message && <p className="text-base text-muted-foreground">{state.message}</p>}
      </Panel>
    );
  }

  if (state?.kind === "done-nowhere" || (state?.kind === "done" && !href)) {
    return (
      <BookingProblem
        title="Your order has been created"
        message="Your confirmation email has the details."
        action={
          <a className="text-sm font-medium text-primary underline-offset-4 hover:underline" href={ctx.links.home()}>
            Back to the site
          </a>
        }
      />
    );
  }

  return (
    <Panel title="Your payment has been successful.">
      <p className="text-base text-muted-foreground">Please wait while we create your order, this can take up to 5 minutes.</p>
      <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" aria-label="Creating your order" />
    </Panel>
  );
}

function Panel({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div role="status" className="mx-auto max-w-lg space-y-4 rounded-lg border bg-card p-8 text-center shadow-sm">
      <h1 className="text-xl font-semibold">{title}</h1>
      {children}
    </div>
  );
}
